import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Landmark, ClipboardCheck, Calculator, FileText, Users, Bot,
    RefreshCw, ExternalLink, ChevronDown, ChevronRight, Check, Minus,
} from 'lucide-react';
import { fetchSmsfAnalytics } from '../api/analyticsApi';
import { syncVirtualContact } from '../../../api/contact';
import toast from 'react-hot-toast';

const formatCurrency = (val) => {
    if (val === null || val === undefined || Number.isNaN(val)) return '—';
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0,
    }).format(val);
};

const formatRelative = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 60) return `${Math.max(1, m)}m ago`;
    const h = Math.floor(diffMs / 3600000);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(diffMs / 86400000);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
};

// Distributions come back keyed by the raw answer strings shown to the buyer,
// so no re-labelling is needed — just a friendly title per question.
const ELIG_LABELS = {
    super_balance: 'Super balance',
    age: 'Age',
    employment: 'Employment',
    owns_investment: 'Already owns investment property',
    goal: 'Primary goal',
};

const StatCard = ({ icon: Icon, label, value, sub, tint = 'blue' }) => {
    const tints = {
        blue: 'bg-blue-50 text-blue-600',
        green: 'bg-emerald-50 text-emerald-600',
        purple: 'bg-purple-50 text-purple-600',
        amber: 'bg-amber-50 text-amber-600',
        teal: 'bg-teal-50 text-teal-600',
        rose: 'bg-rose-50 text-rose-600',
    };
    return (
        <div className="bg-white rounded-lg shadow p-4 flex items-start gap-3">
            <div className={`p-2 rounded-lg flex-shrink-0 ${tints[tint] || tints.blue}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
                <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
                {sub && <p className="text-xs text-gray-500 mt-0.5 truncate">{sub}</p>}
            </div>
        </div>
    );
};

// Horizontal bar list for an answer distribution.
const DistributionCard = ({ title, dist }) => {
    const entries = Object.entries(dist || {}).sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((s, [, n]) => s + n, 0);
    return (
        <div className="bg-white rounded-lg shadow p-5">
            <h4 className="text-sm font-bold text-gray-900 mb-3">{title}</h4>
            {entries.length === 0 ? (
                <p className="text-sm text-gray-500">No responses yet.</p>
            ) : (
                <div className="space-y-2.5">
                    {entries.map(([label, count]) => {
                        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                        return (
                            <div key={label}>
                                <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="text-gray-700 truncate pr-2">{label}</span>
                                    <span className="font-semibold text-gray-900 flex-shrink-0">{count} ({pct}%)</span>
                                </div>
                                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                                    <div className="h-full bg-teal-500" style={{ width: `${pct}%` }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Yes/no tick used in the participants table.
const Did = ({ on }) => (
    on
        ? <Check className="w-4 h-4 text-emerald-500 inline-block" />
        : <Minus className="w-4 h-4 text-gray-300 inline-block" />
);

const SmsfAnalytics = ({ startDate, endDate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [participantsOpen, setParticipantsOpen] = useState(false);
    const [redirectingBuyer, setRedirectingBuyer] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchSmsfAnalytics(startDate, endDate)
            .then((res) => { if (!cancelled) setData(res); })
            .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    const openBuyer = async (buyer) => {
        if (!buyer || redirectingBuyer) return;
        setRedirectingBuyer(true);
        const toastId = toast.loading(`Opening ${buyer.name || buyer.email}…`);
        try {
            const response = await syncVirtualContact(`buyer_${buyer.id}`);
            const contactId = response?.data?.id;
            if (!contactId) throw new Error('Could not resolve contact');
            toast.dismiss(toastId);
            navigate(`/contacts/${contactId}?tab=activity`, {
                state: { returnTo: '/analytics?view=calculators', returnLabel: 'Calculators' },
            });
        } catch (err) {
            toast.error('Could not open buyer profile.', { id: toastId });
        } finally {
            setRedirectingBuyer(false);
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6 flex items-center justify-center h-40">
                <RefreshCw className="w-5 h-5 text-teal-500 animate-spin mr-2" />
                <span className="text-gray-500 text-sm">Loading SMSF analytics…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Invest with your SMSF</h3>
                <p className="text-rose-600 text-sm">{error}</p>
            </div>
        );
    }

    if (!data) return null;

    const {
        summary = {},
        eligibility_breakdown = {},
        specialist_breakdown = {},
        averages = {},
        participants = [],
    } = data;

    const {
        page_views = 0,
        eligibility_started = 0,
        eligibility_completed = 0,
        completion_rate = null,
        calculator_uses = 0,
        enquiries = 0,
        specialist_requests = 0,
        geevee_sessions = 0,
        unique_users = 0,
        authenticated_users = 0,
    } = summary;

    const anonCount = Math.max(0, unique_users - authenticated_users);
    const specialistEntries = Object.entries(specialist_breakdown || {}).sort((a, b) => b[1] - a[1]);

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Landmark className="w-5 h-5 text-teal-600" />
                    Invest with your SMSF
                </h3>
                <p className="text-sm text-gray-500">
                    How buyers are engaging with the SMSF page — eligibility quiz, calculator, enquiries and specialist requests
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <StatCard
                    icon={Users}
                    label="Page views"
                    value={page_views}
                    sub={`${unique_users} unique · ${anonCount} anon`}
                    tint="blue"
                />
                <StatCard
                    icon={ClipboardCheck}
                    label="Eligibility done"
                    value={eligibility_completed}
                    sub={`${eligibility_started} started${completion_rate != null ? ` · ${completion_rate}%` : ''}`}
                    tint="teal"
                />
                <StatCard
                    icon={Calculator}
                    label="Calculator uses"
                    value={calculator_uses}
                    sub={averages.avg_price ? `~${formatCurrency(averages.avg_price)} avg price` : null}
                    tint="green"
                />
                <StatCard
                    icon={FileText}
                    label="Enquiries"
                    value={enquiries}
                    sub="SMSF enquiry form"
                    tint="amber"
                />
                <StatCard
                    icon={Landmark}
                    label="Specialist reqs"
                    value={specialist_requests}
                    sub="Accountant / broker"
                    tint="purple"
                />
                <StatCard
                    icon={Bot}
                    label="GeeVee chats"
                    value={geevee_sessions}
                    sub="Started on this page"
                    tint="rose"
                />
            </div>

            {/* Eligibility answer distributions — the "who they are" picture */}
            <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3">
                    "Am I eligible?" responses
                    <span className="ml-2 text-xs font-normal text-gray-500">
                        {eligibility_completed} completed quiz{eligibility_completed !== 1 ? 'zes' : ''}
                    </span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.keys(ELIG_LABELS).map((key) => (
                        <DistributionCard
                            key={key}
                            title={ELIG_LABELS[key]}
                            dist={eligibility_breakdown[key]}
                        />
                    ))}

                    <div className="bg-white rounded-lg shadow p-5">
                        <h4 className="text-sm font-bold text-gray-900 mb-3">Calculator averages</h4>
                        {calculator_uses === 0 ? (
                            <p className="text-sm text-gray-500">No calculations yet.</p>
                        ) : (
                            <div className="space-y-1.5 text-sm">
                                <div className="flex justify-between"><span className="text-gray-500">Avg purchase price</span><span className="font-medium text-gray-900">{formatCurrency(averages.avg_price)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Avg super balance</span><span className="font-medium text-gray-900">{formatCurrency(averages.avg_balance)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Avg cash required</span><span className="font-medium text-gray-900">{formatCurrency(averages.avg_total_cash_required)}</span></div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Avg annual cash flow</span>
                                    <span className={`font-medium ${averages.avg_annual_cash_flow == null ? 'text-gray-900' : averages.avg_annual_cash_flow >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                        {formatCurrency(averages.avg_annual_cash_flow)}
                                    </span>
                                </div>
                            </div>
                        )}
                        {specialistEntries.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                                <h4 className="text-sm font-bold text-gray-900 mb-2">Specialist requests</h4>
                                <div className="space-y-1.5 text-sm">
                                    {specialistEntries.map(([label, count]) => (
                                        <div key={label} className="flex justify-between">
                                            <span className="text-gray-500 truncate pr-2">{label}</span>
                                            <span className="font-medium text-gray-900">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Who filled out what */}
            <div className="bg-white rounded-lg shadow p-5">
                <button
                    type="button"
                    onClick={() => setParticipantsOpen((v) => !v)}
                    className="w-full flex items-center justify-between text-left"
                    aria-expanded={participantsOpen}
                >
                    <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        {participantsOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                        Who engaged with SMSF
                    </h4>
                    <p className="text-xs text-gray-500">
                        {participants.length} signed-in buyer{participants.length !== 1 ? 's' : ''} · {participantsOpen ? 'Click a row to open their CRM profile' : 'Click to expand'}
                    </p>
                </button>
                {!participantsOpen ? null : participants.length === 0 ? (
                    <p className="text-sm text-gray-500 mt-3">No signed-in buyers have engaged with the SMSF page in this period.</p>
                ) : (
                    <div className="overflow-x-auto mt-3">
                        <table className="min-w-full text-sm">
                            <thead className="text-xs uppercase text-gray-500 border-b border-gray-200">
                                <tr>
                                    <th className="text-left py-2 pr-3">Buyer</th>
                                    <th className="text-center py-2 px-2">Eligibility</th>
                                    <th className="text-left py-2 px-3">Their answers</th>
                                    <th className="text-right py-2 px-2">Calc</th>
                                    <th className="text-right py-2 px-2">Enquiry</th>
                                    <th className="text-right py-2 px-2">Specialist</th>
                                    <th className="text-right py-2 pr-3">Last active</th>
                                    <th className="text-right py-2 pl-2">Profile</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {participants.map((row) => {
                                    const e = row.eligibility || {};
                                    const answerBits = [e.super_balance, e.goal].filter(Boolean).join(' · ');
                                    return (
                                        <tr
                                            key={row.buyer.id}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={() => openBuyer(row.buyer)}
                                        >
                                            <td className="py-2.5 pr-3">
                                                <p className="font-medium text-gray-900">{row.buyer.name}</p>
                                                <p className="text-xs text-gray-500">{row.buyer.email}</p>
                                            </td>
                                            <td className="py-2.5 px-2 text-center"><Did on={row.eligibility_completed} /></td>
                                            <td className="py-2.5 px-3 text-xs text-gray-600 max-w-xs truncate">{answerBits || '—'}</td>
                                            <td className="py-2.5 px-2 text-right text-gray-900">{row.calculator_uses || <span className="text-gray-300">0</span>}</td>
                                            <td className="py-2.5 px-2 text-right text-gray-900">{row.enquiries || <span className="text-gray-300">0</span>}</td>
                                            <td className="py-2.5 px-2 text-right text-gray-900">{row.specialist_requests || <span className="text-gray-300">0</span>}</td>
                                            <td className="py-2.5 pr-3 text-right text-gray-500">{formatRelative(row.last_at)}</td>
                                            <td className="py-2.5 pl-2 text-right">
                                                <ExternalLink className="w-4 h-4 text-teal-500 inline-block" />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SmsfAnalytics;
